import React, { forwardRef, useImperativeHandle } from 'react';
import FormField from '../../../../components/common/FormField';
import { PumpData } from '../../../../models/CompletionModels';
import { useAppDispatch, useAppSelector } from '../../../../hooks/storeHooks';
import { useFormFieldHandler, useFormValidation, validateTabFields } from '../../../../utilities/completionsUtils';
import {
  fetchPumpManufacturerSeries,
  fetchPumpManufacturerTypes,
} from '../../../../services/well-completions/WellCompletionsService';

interface CompletionsPumpsTabProps {
  pumpData: PumpData;
  setPumpData: React.Dispatch<React.SetStateAction<PumpData>>;
  externalValidationErrors?: { [fieldName: string]: string };
  updateValidationError: (tabname: string, errors: { [fieldName: string]: string }) => void;
}

const CompletionsPumpsTab = forwardRef<any, CompletionsPumpsTabProps>(
  ({ pumpData, setPumpData, externalValidationErrors = {}, updateValidationError }, ref) => {
    const dispatch = useAppDispatch();

    const pumpManufacturers = useAppSelector((state: any) => state.CreateCompletions.pumpManufacturers.data);
    const pump1Series = useAppSelector((state: any) => state.CreateCompletions.pump1ManufacturerSeries.data);
    const pump1SeriesLoading = useAppSelector((state: any) => state.CreateCompletions.pump1ManufacturerSeries.loading);
    const pump1Types = useAppSelector((state: any) => state.CreateCompletions.pump1ManufacturerTypes.data ?? []);
    const pump1TypesLoading = useAppSelector((state: any) => state.CreateCompletions.pump1ManufacturerTypes.loading);
    const pump2Series = useAppSelector((state: any) => state.CreateCompletions.pump2ManufacturerSeries.data);
    const pump2SeriesLoading = useAppSelector((state: any) => state.CreateCompletions.pump2ManufacturerSeries.loading);
    const pump2Types = useAppSelector((state: any) => state.CreateCompletions.pump2ManufacturerTypes.data ?? []);
    const pump2TypesLoading = useAppSelector((state: any) => state.CreateCompletions.pump2ManufacturerTypes.loading);
    const pump3Series = useAppSelector((state: any) => state.CreateCompletions.pump3ManufacturerSeries.data);
    const pump3SeriesLoading = useAppSelector((state: any) => state.CreateCompletions.pump3ManufacturerSeries.loading);
    const pump3Types = useAppSelector((state: any) => state.CreateCompletions.pump3ManufacturerTypes.data ?? []);
    const pump3TypesLoading = useAppSelector((state: any) => state.CreateCompletions.pump3ManufacturerTypes.loading);

    const validation = useFormValidation({ tabName: 'pumps' });
    const updateValidation = (updatedData: PumpData) => {
      const fieldErrors = validateTabFields('pumps', updatedData);
      validation.setErrors?.(fieldErrors);
      updateValidationError('pumps', fieldErrors);
    };
    const handleFieldChange = useFormFieldHandler(setPumpData, validation, updateValidation);

    const errors = Object.keys(externalValidationErrors).length > 0 ? externalValidationErrors : validation.errors;

    const pumpManufacturerOptions = Array.isArray(pumpManufacturers)
      ? pumpManufacturers.map((m: string) => ({
        key: m,
        value: m,
      }))
      : [];

    const pump1SeriesOptions = Array.isArray(pump1Series)
      ? pump1Series.map((s: any) => ({
        key: s,
        value: s,
      }))
      : [];

    const pump1TypeOptions = Array.isArray(pump1Types)
      ? pump1Types.map((s: any) => ({
        key: s.Type,
        value: s.Type,
      }))
      : [];

    const pump2SeriesOptions = Array.isArray(pump2Series)
      ? pump2Series.map((s: any) => ({
        key: s,
        value: s,
      }))
      : [];

    const pump2TypeOptions = Array.isArray(pump2Types)
      ? pump2Types.map((s: any) => ({
        key: s.Type,
        value: s.Type,
      }))
      : [];

    const pump3SeriesOptions = Array.isArray(pump3Series)
      ? pump3Series.map((s: any) => ({
        key: s,
        value: s,
      }))
      : [];

    const pump3TypeOptions = Array.isArray(pump3Types)
      ? pump3Types.map((s: any) => ({
        key: s.Type,
        value: s.Type,
      }))
      : [];

    useImperativeHandle(ref, () => ({
      triggerValidation: (data: PumpData) => {
        validation.validateAllFields(data);
      },
    }));

    const resetDependentFields = (field: keyof PumpData) => {
      switch (field) {
        case 'pumpUpperManufacturer':
          handleFieldChange('pumpUpperSeries', '');
          handleFieldChange('pumpUpperType', '');
          break;
        case 'pumpUpperSeries':
          handleFieldChange('pumpUpperType', '');
          break;
        case 'pumpMidManufacturer':
          handleFieldChange('pumpMidSeries', '');
          handleFieldChange('pumpMidType', '');
          break;
        case 'pumpMidSeries':
          handleFieldChange('pumpMidType', '');
          break;
        case 'pumpLowerManufacturer':
          handleFieldChange('pumpLowerSeries', '');
          handleFieldChange('pumpLowerType', '');
          break;
        case 'pumpLowerSeries':
          handleFieldChange('pumpLowerType', '');
          break;
        default:
          break;
      }
    };

    const handleManufacturerChange = (field: keyof PumpData, value: string) => {
      resetDependentFields(field);
      handleFieldChange(field, value);
      dispatch(fetchPumpManufacturerSeries({ field, manufacturer: value }));
    };

    const handleSeriesChange = (field: keyof PumpData, manufacturer: string | null, series: string) => {
      resetDependentFields(field);
      handleFieldChange(field, series);
      if (manufacturer) {
        dispatch(fetchPumpManufacturerTypes({ field, manufacturer, series }));
      }
    };

    return (
      <>
        <div className='pump-heading'>
          <h3>Upper section</h3>
          <hr />
        </div>
        <div className='pump-form'>
          <div className='pump-form-row'>
            <FormField
              label='Pump manufacturer'
              name='pumpUpperManufacturer'
              type='select'
              value={pumpData.pumpUpperManufacturer}
              onChange={(_, v) => {
                handleManufacturerChange('pumpUpperManufacturer', v as string);
              }}
              options={pumpManufacturerOptions}
              error={errors.pumpUpperManufacturer}
              required
            />
            <FormField
              label='Pump series'
              name='pumpUpperSeries'
              type='select'
              value={pumpData.pumpUpperSeries}
              isLoading={pump1SeriesLoading}
              onChange={(_, v) => {
                handleSeriesChange('pumpUpperSeries', pumpData.pumpUpperManufacturer, v as string);
              }}
              error={errors.pumpUpperSeries}
              options={pump1SeriesOptions}
              required
            />
          </div>

          <div className='pump-form-row'>
            <FormField
              label='Pump type'
              name='pumpUpperType'
              type='select'
              value={pumpData.pumpUpperType}
              onChange={(_, v) => {
                const selected = pump1Types.find((t: any) => t.Type === v);
                handleFieldChange('pumpUpperType', v);
                handleFieldChange('pumpUpperId', selected ? selected.Id : null);
              }}
              isLoading={pump1TypesLoading}
              options={pump1TypeOptions}
              error={errors.pumpUpperType}
              required
            />
            <FormField
              label='Pump 1 # stages'
              name='pumpUpperTVD'
              type='number'
              value={pumpData.pumpUpperTVD}
              onChange={(_, v) => handleFieldChange('pumpUpperTVD', v)}
              error={errors.pumpUpperTVD}
              required
            />
          </div>
        </div>
        <div className='pump-heading'>
          <h3>Mid section</h3>
          <hr />
        </div>
        <div className='pump-form'>
          <div className='pump-form-row'>
            <FormField
              label='Pump manufacturer'
              name='pumpMidManufacturer'
              type='select'
              value={pumpData.pumpMidManufacturer}
              onChange={(_, v) => {
                handleManufacturerChange('pumpMidManufacturer', v as string);
              }}
              options={pumpManufacturerOptions}
              error={errors.pumpMidManufacturer}
            />
            <FormField
              label='Pump series'
              name='pumpMidSeries'
              type='select'
              value={pumpData.pumpMidSeries}
              onChange={(_, v) => {
                handleSeriesChange('pumpMidSeries', pumpData.pumpMidManufacturer, v as string);
              }}
              options={pump2SeriesOptions}
              isLoading={pump2SeriesLoading}
            />
          </div>

          <div className='pump-form-row'>
            <FormField
              label='Pump type'
              name='pumpMidType'
              type='select'
              value={pumpData.pumpMidType}
              onChange={(_, v) => {
                const selected = pump2Types.find((t: any) => t.Type === v);
                handleFieldChange('pumpMidType', v);
                handleFieldChange('pumpMidId', selected ? selected.Id : null);
              }}
              options={pump2TypeOptions}
              isLoading={pump2TypesLoading}
            />
            <FormField
              name='pumpMidTVD'
              label='Pump 2 # stages'
              type='number'
              value={pumpData.pumpMidTVD}
              onChange={(_, v) => handleFieldChange('pumpMidTVD', v)}
              error={errors.pumpMidTVD}
            />
          </div>
        </div>
        <div className='pump-heading'>
          <h3>Lower section</h3>
          <hr />
        </div>
        <div className='pump-form'>
          <div className='pump-form-row'>
            <FormField
              label='Pump manufacturer'
              name='pumpLowerManufacturer'
              type='select'
              value={pumpData.pumpLowerManufacturer}
              onChange={(_, v) => {
                handleManufacturerChange('pumpLowerManufacturer', v as string);
              }}
              options={pumpManufacturerOptions}
              error={errors.pumpLowerManufacturer}
            />
            <FormField
              label='Pump series'
              name='pumpLowerSeries'
              type='select'
              value={pumpData.pumpLowerSeries}
              onChange={(_, v) => {
                handleSeriesChange('pumpLowerSeries', pumpData.pumpLowerManufacturer, v as string);
              }}
              options={pump3SeriesOptions}
              isLoading={pump3SeriesLoading}
            />
          </div>

          <div className='pump-form-row'>
            <FormField
              label='Pump type'
              name='pumpLowerType'
              type='select'
              value={pumpData.pumpLowerType}
              onChange={(_, v) => {
                const selected = pump3Types.find((t: any) => t.Type === v);
                handleFieldChange('pumpLowerType', v);
                handleFieldChange('pumpLowerId', selected ? selected.Id : null);
              }}
              options={pump3TypeOptions}
              isLoading={pump3TypesLoading}
            />
            <FormField
              label='Pump 3 # stages'
              name='pumpLowerTVD'
              type='number'
              value={pumpData.pumpLowerTVD}
              onChange={(_, v) => handleFieldChange('pumpLowerTVD', v)}
              error={errors.pumpLowerTVD}
            />
          </div>
          <div className='pump-form-row'>
            <FormField
              label='Liquid separation efficiency (%)'
              name='pumpLowerEfficiency'
              type='number'
              value={pumpData.liquidSeparationEfficiency}
              onChange={(_, v) => handleFieldChange('liquidSeparationEfficiency', v)}
              error={errors.liquidSeparationEfficiency}
            />
            <FormField
              label='Gas separation efficiency (%)'
              name='gasSeparationEfficiency'
              type='number'
              value={pumpData.gasSeparationEfficiency}
              onChange={(_, v) => handleFieldChange('gasSeparationEfficiency', v)}
              error={errors.gasSeparationEfficiency}
            />
          </div>
          <div className='pump-form-row'>
            <div className='pump-form-checkbox'>
              <div
                className={`checkbox ${pumpData.gasSeparator ? 'enabled' : ''}`}
                onClick={() => handleFieldChange('gasSeparator', !pumpData.gasSeparator)}
              >
                {pumpData.gasSeparator && <div className='checkbox-icon' />}
              </div>
              <span
                style={{ marginLeft: 8, cursor: 'pointer' }}
                onClick={() => handleFieldChange('gasSeparator', !pumpData.gasSeparator)}
              >
                Gas separator
              </span>
            </div>
            <div className='pump-form-checkbox'>
              <div
                className={`checkbox ${pumpData.co2Flood ? 'enabled' : ''}`}
                onClick={() => handleFieldChange('co2Flood', !pumpData.co2Flood)}
              >
                {pumpData.co2Flood && <div className='checkbox-icon' />}
              </div>
              <span
                style={{ marginLeft: 8, cursor: 'pointer' }}
                onClick={() => handleFieldChange('co2Flood', !pumpData.co2Flood)}
              >
                Co2 flood
              </span>
            </div>
          </div>
          <div className='pump-form-row'>
            <div className='pump-form-checkbox'>
              <div
                className={`checkbox ${pumpData.permanentMagnetMotor ? 'enabled' : ''}`}
                onClick={() => handleFieldChange('permanentMagnetMotor', !pumpData.permanentMagnetMotor)}
              >
                {pumpData.permanentMagnetMotor && <div className='checkbox-icon' />}
              </div>
              <span
                style={{ marginLeft: 8, cursor: 'pointer' }}
                onClick={() => handleFieldChange('permanentMagnetMotor', !pumpData.permanentMagnetMotor)}
              >
                Permanent magnetic motor
              </span>
            </div>
            <div className='pump-form-checkbox'>
              <div
                className={`checkbox ${pumpData.subcool ? 'enabled' : ''}`}
                onClick={() => handleFieldChange('subcool', !pumpData.subcool)}
              >
                {pumpData.subcool && <div className='checkbox-icon' />}
              </div>
              <span
                style={{ marginLeft: 8, cursor: 'pointer' }}
                onClick={() => handleFieldChange('subcool', !pumpData.subcool)}
              >
                Subcool
              </span>
            </div>
          </div>
        </div>
      </>
    );
  },
);

export default CompletionsPumpsTab;
